import { ResponsiveBar } from "@nivo/bar";
import { last } from "lodash";
import React, { useMemo, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import { StringParam, useQueryParams } from "use-query-params";
import { SkillFragment } from "../../graphql/fragments";
import useGetReportAnswers from "../../graphql/hooks/queries/useGetReportAnswers";
import useTranslations from "../../hooks/useTranslations";
import {
  getReferentGradeAverage,
  getTalentUserData,
} from "../../utils/madUtils";
import { AnalyticsIcon } from "../icons";
import {
  Avatar,
  Container,
  H4,
  Link,
  ScreenContainer,
  ScreenHeader,
  Tab,
  TabList,
  Tabs,
} from "../wombat-ui";
import SkillEvolutionDetail from "./SkillEvolutionDetail";

type SkillAnalyticsDetailProps = RouteComponentProps<{ id: string }>;

const SkillAnalyticsDetail: React.FC<SkillAnalyticsDetailProps> = ({
  match,
}) => {
  const t = useTranslations();

  const { id } = match.params;
  const [, setQuery] = useQueryParams({
    userId: StringParam,
  });

  const { data, loading } = useGetReportAnswers({
    variables: { skillIds: [id], isCheckedReport: true },
  });

  const [selectedDataset, setSelectedDataset] = useState<"talent" | "referent">(
    "referent"
  );

  const skill = useMemo(() => {
    if (data)
      return data.reportAnswers.collection?.[0]?.templateQuestion
        .subject as SkillFragment;
  }, [data]);

  const chartData = useMemo(() => {
    if (data) {
      return getTalentUserData(data.reportAnswers.collection).map(
        ({ user, reports }) => {
          const lastReport = last(reports);

          console.log(lastReport.reportAnswers);
          const referentGradeAverage = getReferentGradeAverage(
            lastReport.reportAnswers,
            lastReport.reportAnswers
              .filter((ra) => !ra.isTalent)
              .map(({ user }) => user.id)
          )?.[0];

          const talentReportAnswer = lastReport.reportAnswers.filter(
            (ra) => ra.isTalent
          )?.[0];
          const talentGrade = {
            talentGrade: parseInt(talentReportAnswer.value.selector),
            talentGradeLabel: talentReportAnswer.templateQuestion.options.find(
              (o) => o.value === talentReportAnswer.value.selector
            )?.label,
          };

          return {
            id: user.id,
            templateQuestion: lastReport.reportAnswers?.[0].templateQuestion,
            name: user.name,
            referentGrade: referentGradeAverage?.averageGrade,
            referentGradeLabel: referentGradeAverage?.averageGradeLabel,
            ...talentGrade,
            avatar: user.avatar,
          };
        }
      );
    }
    return null;
  }, [data]);

  const experts = useMemo(
    () =>
      chartData?.filter(
        ({ referentGrade, talentGrade }) =>
          (selectedDataset === "referent" ? referentGrade : talentGrade) ===
          last(chartData)?.templateQuestion.options.length - 1
      ),
    [chartData, selectedDataset]
  );

  return (
    <ScreenContainer loading={loading}>
      <ScreenHeader
        title={t("statistics.statistics")}
        description={t("statistics.statisticsDescription")}
        icon={AnalyticsIcon}
        actions={[
          <Link
            key={"back"}
            type="button"
            variant="button-subtle"
            to={"/statistics"}
          >
            {t("header.goBack")}
          </Link>,
        ]}
      ></ScreenHeader>

      <Container className="flex flex-col w-full">
        <Tabs>
          <TabList>
            {/* Using onMouseUp because onClik is reserved by ReachUI */}
            <Tab onMouseUp={() => setSelectedDataset("referent")}>
              Vision référent
            </Tab>
            <Tab onMouseUp={() => setSelectedDataset("talent")}>
              Vision talent
            </Tab>
          </TabList>
        </Tabs>
        <div className="grid grid-cols-8 gap-10 mx-12 mt-12 mb-6 min-h-lg">
          <div className="flex flex-col flex-grow col-span-5 px-3 py-2 space-y-2 rounded-md shadow-md">
            <H4>
              {t("statistics.collaboratorEvaluatedOn", { value: skill?.name })}
            </H4>
            <ResponsiveBar
              data={chartData}
              keys={[`${selectedDataset}Grade`]}
              margin={{ top: 30, right: 30, bottom: 100, left: 30 }}
              padding={0.3}
              valueScale={{ type: "linear" }}
              indexScale={{ type: "band", round: true }}
              colors={{ scheme: "nivo" }}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 45,
                format: (value) => {
                  const entry = chartData.find(({ id }) => id === value);
                  return entry.name;
                },
                legendPosition: "middle",
                legendOffset: 32,
              }}
              onClick={({ data }) => setQuery({ userId: data.id as string })}
              axisLeft={{
                tickSize: 5,
              }}
              minValue={0}
              maxValue={4}
              labelSkipWidth={12}
              labelSkipHeight={12}
              animate={true}
              label={({ data }) => data[`${selectedDataset}GradeLabel`]}
              tooltip={() => (
                <span className="text-sm text-dark-100">
                  {t("statistics.clickToSeeDetails")}
                </span>
              )}
              labelTextColor={{
                from: "color",
                modifiers: [["darker", 1.6]],
              }}
            />
          </div>
          <div className="col-span-3">
            <div className="flex flex-col h-full px-4 py-3 space-y-2 bg-white rounded-md shadow-md">
              <H4>
                {t("statistics.collaboratorEvaluatedAsIn", {
                  as: last(last(chartData)?.templateQuestion.options)?.label,
                  in: skill?.name,
                })}
              </H4>

              {experts?.map(({ name, avatar, id }) => (
                <div className="flex flex-col py-1" key={id}>
                  <div className="flex items-center">
                    <Avatar src={avatar} type="user" size={8} alt={name} />
                    <span className="ml-3 text-sm text-light-900">{name}</span>
                  </div>
                </div>
              ))}
              {experts?.length === 0 && (
                <div className="flex flex-col items-center justify-center h-full">
                  <span className="text-sm text-light-900">
                    {t("statistics.noCollaboratorsEvaluatedAs", {
                      value: last(last(chartData)?.templateQuestion.options)
                        ?.label,
                    })}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
        <SkillEvolutionDetail id={id} selectedDataset={selectedDataset} />
      </Container>
    </ScreenContainer>
  );
};

export default React.memo(SkillAnalyticsDetail);
